import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { Swords, Check, X, Loader2 } from "lucide-react";

const REC = "#F472B6";
const STATUS_COLORS = { pending: "#F59E0B", accepted: "#A3E635", declined: "#EF4444" };

export default function RecChallenges() {
  const [data, setData] = useState({ incoming: [], outgoing: [] });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [message, setMessage] = useState("");
  const [sugs, setSugs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try { const { data: d } = await api.get("/rec-games/challenges"); setData(d); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const search = (q) => {
    setQuery(q);
    setPicked(null);
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setSugs([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data: d } = await api.get("/members/search", { params: { q } }); setSugs(d); }
      catch { setSugs([]); }
    }, 250);
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/rec-games/challenges", { opponent_id: picked.id, message });
      toast.success(`Challenge sent to ${d.opponent_name} ⚔️`);
      setOpen(false); setQuery(""); setPicked(null); setMessage("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const respond = async (c, accept) => {
    setActing(c.id);
    try {
      await api.post(`/rec-games/challenges/${c.id}/respond`, { accept });
      toast.success(accept ? "Challenge accepted — go play, then log the game! ⚔️" : "Challenge declined");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActing(null); }
  };

  return (
    <div className="mb-5" data-testid="rec-challenges">
      {data.incoming.length > 0 && (
        <div className="space-y-2 mb-3" data-testid="rec-challenges-incoming">
          {data.incoming.map((c) => (
            <div key={c.id} data-testid={`challenge-${c.id}`} className="flex items-center gap-3 border border-[#F472B6]/50 bg-[#F472B6]/10 px-3 py-2.5">
              <Swords className="w-4 h-4 shrink-0" style={{ color: REC }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate"><b>{c.challenger_name}</b> challenged you!</div>
                {c.message && <div className="text-[11px] text-zinc-400 truncate">“{c.message}”</div>}
              </div>
              <button data-testid={`challenge-accept-${c.id}`} onClick={() => respond(c, true)} disabled={acting === c.id}
                className="flex items-center gap-1 text-black text-[10px] font-black uppercase tracking-widest px-3 py-2 active:scale-95 transition-all disabled:opacity-50"
                style={{ background: REC }}>
                {acting === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Accept
              </button>
              <button data-testid={`challenge-decline-${c.id}`} onClick={() => respond(c, false)} disabled={acting === c.id}
                className="flex items-center gap-1 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
                <X className="w-3 h-3" /> Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {!open ? (
        <button data-testid="issue-challenge-btn" onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 border border-[#F472B6] text-[#F472B6] hover:bg-[#F472B6]/10 font-black uppercase tracking-widest py-3 text-xs active:scale-[0.98] transition-all">
          <Swords className="w-4 h-4" /> Issue a Challenge
        </button>
      ) : (
        <div className="border border-[#F472B6]/50 bg-pb-elevated/40 p-4 space-y-3" data-testid="challenge-form">
          <div className="relative">
            <input data-testid="challenge-opponent" value={picked ? picked.name : query} placeholder="Who are you calling out?"
              onChange={(e) => search(e.target.value)} autoComplete="off"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#F472B6]" />
            {sugs.length > 0 && !picked && (
              <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-pb-surface border border-[#F472B6]/50 max-h-48 overflow-y-auto shadow-xl">
                {sugs.map((m) => (
                  <button key={m.id} type="button" data-testid={`challenge-sug-${m.id}`}
                    onClick={() => { setPicked(m); setSugs([]); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-pb-elevated transition-colors border-b border-pb-border/40 last:border-0">
                    <Avatar src={m.avatar} name={m.name} size={26} />
                    <span className="flex-1 text-sm text-white truncate">{m.name}</span>
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500">{m.membership}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input data-testid="challenge-message" value={message} placeholder="Trash talk (optional)" maxLength={200}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#F472B6]" />
          <div className="flex gap-2">
            <button data-testid="challenge-send" onClick={send} disabled={busy || !picked}
              className="flex-1 text-black font-black uppercase tracking-widest py-2.5 text-xs active:scale-95 transition-all disabled:opacity-40"
              style={{ background: REC }}>
              {busy ? "…" : "Send Challenge ⚔️"}
            </button>
            <button data-testid="challenge-cancel" onClick={() => { setOpen(false); setPicked(null); setQuery(""); }}
              className="border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-4 hover:text-white transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {data.outgoing.length > 0 && (
        <div className="space-y-1.5 mt-3" data-testid="rec-challenges-outgoing">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your challenges</div>
          {data.outgoing.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-xs px-3 py-2 bg-pb-elevated/40 border border-pb-border/50">
              <span className="text-zinc-300 truncate">vs {c.opponent_name}{c.message ? ` · “${c.message}”` : ""}</span>
              <span className="font-bold uppercase tracking-widest text-[9px]" style={{ color: STATUS_COLORS[c.status] || "#71717A" }}>{c.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
